// Merge… (spec/dashboard.md section 12.3; spec/api-conventions.md sections 12.1–12.3): on open the dry run
// (`:merge { dryRun: true }`) shows what merging changes on the target and any conflicts; then Merge with a message
// (default `Merge request !<number>: <title>`). The button waits for a mergeable request and asks the target's permission
// (`content_write`, or `deploy` on production for a protected target). Nothing asks for the target to be deployed first.
// The merged toast leads on (section 12.3): Build a release of <target>, View commit.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ops } from '../../api/operations';
import type { BranchOperation, MergeRequest } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { ChangesGrid } from '../../history/ChangesGrid';
import type { ContentChange } from '../../history/changes';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Field';
import { TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { useToasts } from '../../ui/Toast';
import { asApiError, mergeBlock, shortSha } from './merge';
import { LoadError, SkeletonRows } from '../../ui/PageStates';

export interface MergeDialogProps {
  mr: MergeRequest;
  onClose: () => void;
  /** after a merge (or an "already merged"): re-read the request */
  onMerged: (result: BranchOperation) => void;
}

export function MergeDialog({ mr, onClose, onMerged }: MergeDialogProps) {
  const { org, game, can } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const navigate = useNavigate();
  const id = String(mr.number);
  const [preview, setPreview] = useState<BranchOperation | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [message, setMessage] = useState(`Merge request !${mr.number}: ${mr.title}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    client.data(ops.mergeMergeRequest, { path: { org, game: game.slug, mr: id }, body: { dryRun: true } }).then(
      (r) => !cancelled && setPreview(r as unknown as BranchOperation),
      (e: unknown) => {
        if (cancelled) return;
        const err = asApiError(e);
        setPreviewError(err.message + (err.requestId ? ` (request ${err.requestId})` : ''));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [client, org, game.slug, id]);

  const permission = mr.targetProtected ? (can('deploy', 'prod') ? null : 'deploy') : can('content_write') ? null : 'content_write';
  const block = mergeBlock(mr);

  const merge = async () => {
    if (busy || block) return;
    setBusy(true);
    setError(null);
    try {
      const text = message.trim();
      const result = (await client.data(ops.mergeMergeRequest, { path: { org, game: game.slug, mr: id }, body: (text ? { message: text } : {}) })) as unknown as BranchOperation;
      if (!result.changed) toasts.info('Already merged — nothing to do.');
      else {
        const target = mr.targetBranch;
        const commit = result.commit;
        toasts.success(`Merged !${mr.number} into ${target} at ${shortSha(commit)}`, {
          actions: [
            { label: `Build a release of ${target}`, onClick: () => navigate(`${routes.releases(org, game.slug, target, 'releases')}?build=1`) },
            ...(commit ? [{ label: 'View commit', onClick: () => navigate(routes.history(org, game.slug, target, commit)) }] : []),
          ],
        });
      }
      onMerged(result);
      onClose();
    } catch (e) {
      const err = asApiError(e);
      setError(err.message + (err.requestId ? ` (request ${err.requestId})` : ''));
    } finally {
      setBusy(false);
    }
  };

  const conflicts = preview?.conflicts ?? [];
  return (
    <Modal
      open
      size="l"
      title={`Merge !${mr.number} into ${mr.targetBranch}`}
      onClose={() => !busy && onClose()}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void merge()} busy={busy} disabled={block !== null} missingPermission={permission} title={block ?? `Merge ${mr.sourceBranch} into ${mr.targetBranch}`}>
            Merge
          </Button>
        </>
      }
    >
      <div data-testid="merge-dialog" className="mr-merge-dialog">
        <p className="muted small">
          <code>{mr.sourceBranch}</code> → <code>{mr.targetBranch}</code>
          {mr.targetProtected ? ' · the target is protected: merging needs the production deploy permission and a current approval' : ''}
        </p>
        <h3>Dry run</h3>
        {previewError ? (
          <LoadError>{previewError}</LoadError>
        ) : !preview ? (
          <SkeletonRows rows={3} label="Running the dry run" />
        ) : (
          <>
            <div data-testid="merge-preview-changes">
              <ChangesGrid changes={(preview.changes ?? []) as ContentChange[]} branch={mr.targetBranch} emptyMessage="Merging changes nothing on the target." />
            </div>
            {conflicts.length > 0 && (
              <div data-testid="merge-preview-conflicts" className="mr-merge-conflicts">
                <strong>{conflicts.length === 1 ? '1 conflict' : `${conflicts.length} conflicts`}</strong>
                <ul>
                  {conflicts.map((c) => (
                    <li key={c.path}>
                      <code>{c.path}</code>
                      {c.name ? ` — ${c.name}` : ''}
                      {c.fields?.length ? <span className="muted small"> ({c.fields.join(', ')})</span> : null}
                      {c.resolution ? <span className="muted small"> · resolved: {c.resolution}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
        <Field label="Message">
          <TextInput aria-label="Message" value={message} onChange={(e) => setMessage(e.target.value)} />
        </Field>
        {Number(mr.comments?.unresolved ?? 0) > 0 && (
          <p className="mr-merge-comments small" data-testid="merge-comments-warning">
            {Number(mr.comments!.unresolved) === 1 ? '1 unresolved comment' : `${mr.comments!.unresolved} unresolved comments`}: merge anyway?
          </p>
        )}
        {block && (
          <p className="muted small" data-testid="merge-block">
            {block}
          </p>
        )}
        {error && (
          <LoadError>{error}</LoadError>
        )}
      </div>
    </Modal>
  );
}
